import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import {
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalTransition,
	ModalTitle,
} from '@atlaskit/modal-dialog';
import { Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import type { ApprovalStatus } from '../ApprovalsContext.tsx';

const i18n = defineMessages({
	replaceApprovalButtonLabel: {
		id: 'approvals.replace-approval.modal.replace-approval.button.label',
		defaultMessage: 'Confirm',
		description: 'Label for a button to confirm replacing the approval by publishing',
	},
	approvedModalHeading: {
		id: 'approvals.replace-approval.modal.heading.approved',
		defaultMessage: 'Proceed with replacing approved content?',
		description:
			'Heading for the replace approval modal shown when publishing would replace previously approved content',
	},
	approvedModalDescription: {
		id: 'approvals.replace-approval.modal.description.approved',
		defaultMessage:
			'The published version of this page has been previously reviewed and approved. Proceeding with publish will replace the content and clear the approved status from the page.',
		description:
			'Description explaining the consequences of publishing over a page with previously approved content',
	},
	pendingModalHeading: {
		id: 'approvals.replace-approval.modal.heading.pending',
		defaultMessage: 'Proceed and cancel pending review?',
		description:
			'Heading for the replace approval modal shown when publishing would cancel a pending review',
	},
	pendingModalDescription: {
		id: 'approvals.replace-approval.modal.description.pending',
		defaultMessage:
			'The published version of this page has a review pending. Proceeding with publish will replace the content and cancel the review in progress.',
		description:
			'Description explaining the consequences of publishing over a page with a pending review',
	},
});

const styles = cssMap({
	footerRow: {
		justifyContent: 'flex-end',
		gap: token('space.150'),
		marginTop: token('space.200'),
	},
});

type ReplaceApprovalModalFooterOptionsProps = {
	onConfirm: () => void;
};

const ReplaceApprovalModalFooterOptions: FC<ReplaceApprovalModalFooterOptionsProps> = ({
	onConfirm,
}) => {
	return (
		<Inline xcss={styles.footerRow}>
			<Button appearance="warning" onClick={onConfirm}>
				<FormattedMessage {...i18n.replaceApprovalButtonLabel} />
			</Button>
		</Inline>
	);
};

type ReplaceApprovalModalProps = {
	otherModeReviewStatus: ApprovalStatus | null;
	onConfirm: () => void;
	onClose: () => void;
};

const getModalCopy = (otherModeReviewStatus: ApprovalStatus | null) => {
	if (otherModeReviewStatus === 'PENDING') {
		return {
			heading: i18n.pendingModalHeading,
			description: i18n.pendingModalDescription,
		};
	}

	return {
		heading: i18n.approvedModalHeading,
		description: i18n.approvedModalDescription,
	};
};

export const ReplaceApprovalModal: FC<ReplaceApprovalModalProps> = ({
	otherModeReviewStatus,
	onConfirm,
	onClose,
}) => {
	const { Modal } = useChoreographerAssets();
	const { heading, description } = getModalCopy(otherModeReviewStatus);

	return (
		<ModalTransition>
			<Modal onClose={onClose}>
				<ModalHeader hasCloseButton>
					<ModalTitle appearance="warning">
						<FormattedMessage {...heading} />
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<FormattedMessage {...description} />
				</ModalBody>
				<ModalFooter>
					<ReplaceApprovalModalFooterOptions onConfirm={onConfirm} />
				</ModalFooter>
			</Modal>
		</ModalTransition>
	);
};
